import {
  fetchGuideDocx,
  fetchGuidePdf,
  GuideShare,
  guideQueryOptions,
  useGuideShareUpdateMutation,
} from '@blesk-frontend/api';
import {
  Button,
  copyAsHtml,
  copyAsMarkdown,
  Modal,
  ModalProps,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@blesk-frontend/ui';
import {useQuery} from '@tanstack/react-query';
import {useState} from 'react';
import {toast} from 'sonner';

import {copyToClipboard} from '@/libs';
import {sendEvent} from '@/libs/analytics';

interface GuideShareModalProps extends ModalProps {
  guideId?: string;
}

export const GuideShareModal = ({guideId, onClose, ...props}: GuideShareModalProps) => {
  const {data: guide} = useQuery(guideQueryOptions(guideId || ''));
  const [isCopyingMD, setIsCopyingMD] = useState(false);
  const [isCopyingHTML, setIsCopyingHTML] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingDocx, setIsExportingDocx] = useState(false);

  const {
    mutateAsync: mutateAsyncShare,
    variables: optimisticShare,
    isPending: isPendingShare,
  } = useGuideShareUpdateMutation();

  const onUpdateShare = async (type: GuideShare['type']) => {
    if (!guide) return;
    await mutateAsyncShare({guideId: guide.id, type});
  };
  const onClickCopyLink = async () => {
    if (guide?.share?.type === 'link') {
      const link = `${import.meta.env.VITE_GALLERY_URL}/link/${guide.share.link}`;
      console.log({link});
      await copyToClipboard(link);
    }
    sendEvent('guides/share-copy-link');
    toast.success('Ссылка на инструкцию скопирована в буфер');
  };

  const onClickCopyHtml = async () => {
    if (!guide) return;
    sendEvent('guides/export-copy-html');
    setIsCopyingHTML(true);
    await copyAsHtml(guide).catch((error) => {
      console.error(error);
      toast.error('Ошибка при экспорте HTML');
      setIsCopyingHTML(false);
      return;
    });
    sendEvent('guides/export-success');
    setIsCopyingHTML(false);
    toast.success('Данные скопированы в буфер');
  };

  const onClickCopyMd = async () => {
    if (!guide) return;
    sendEvent('guides/export-copy-md');
    setIsCopyingMD(true);
    await copyAsMarkdown(guide).catch((error) => {
      console.error(error);
      toast.error('Ошибка при экспорте Markdown');
      setIsCopyingMD(false);
      return;
    });
    sendEvent('guides/export-success');
    setIsCopyingMD(false);
    toast.success('Данные скопированы в буфер');
  };

  const onClickExportPDF = async () => {
    if (!guide) return;
    sendEvent('guides/export-pdf');
    setIsExportingPDF(true);
    const pdf = await fetchGuidePdf(guide.id).catch((error) => {
      console.error(error);
      toast.error('Ошибка при экспорте PDF');
      setIsExportingPDF(false);
      return;
    });
    const url = URL.createObjectURL(pdf);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${guide.name}.pdf`;
    a.click();
    sendEvent('guides/export-success');
    setIsExportingPDF(false);
    toast.success('PDF документ готов');
  };

  const onClickExportDocx = async () => {
    if (!guide) return;
    sendEvent('guides/export-docx');
    setIsExportingDocx(true);
    const docx = await fetchGuideDocx(guide.id).catch((error) => {
      console.error(error);
      toast.error('Ошибка при экспорте Word');
      setIsExportingDocx(false);
      return;
    });
    const url = URL.createObjectURL(docx);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${guide.name}.docx`;
    a.click();
    sendEvent('guides/export-success');
    setIsExportingDocx(false);
    toast.success('Word документ готов');
  };

  return (
    <Modal onClose={onClose} {...props}>
      <div className="mb-6 flex flex-col gap-3">
        <h1 className="w-max text-title-h1">Поделиться</h1>
        <p className="text-paragraph-md">
          Поделитесь ссылкой на инструкцию или сохраните в&nbsp;удобном формате
        </p>
      </div>

      <div className="flex flex-col gap-4 overflow-hidden">
        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <Select
            value={isPendingShare ? optimisticShare.type : guide?.share.type}
            onValueChange={onUpdateShare}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Доступно только вам" />
            </SelectTrigger>
            <SelectContent className="z-[3000]">
              <SelectItem value="private">Доступно только вам</SelectItem>
              <SelectItem value="link">Доступно всем по ссылке</SelectItem>
            </SelectContent>
          </Select>
          <Button
            className="w-[112px]"
            onClick={onClickCopyLink}
            disabled={
              isPendingShare ? optimisticShare.type === 'private' : guide?.share.type === 'private'
            }
            loading={isPendingShare}
          >
            Ссылка
          </Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <p className="font-medium">Сохранить в Word</p>
            <p className="text-paragraph-sm">Для работы в привычном формате</p>
          </div>
          <Button
            variant="outline"
            className="w-[112px]"
            onClick={onClickExportDocx}
            loading={isExportingDocx}
          >
            Сохранить
          </Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <p className="font-medium">Сохранить в PDF</p>
            <p className="text-paragraph-sm">Чтобы переслать по почте</p>
          </div>
          <Button
            variant="outline"
            className="w-[112px]"
            onClick={onClickExportPDF}
            loading={isExportingPDF}
          >
            Сохранить
          </Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <p className="font-medium">Экспорт в HTML</p>
            <p className="text-paragraph-sm">Вставить в Word, Google Docs</p>
          </div>
          <Button
            variant="outline"
            className="w-[112px]"
            onClick={onClickCopyHtml}
            loading={isCopyingHTML}
          >
            Копировать
          </Button>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <p className="font-medium">Экспорт в Markdown</p>
            <p className="text-paragraph-sm">Вставить в Notion и Github</p>
          </div>
          <Button
            variant="outline"
            className="w-[112px]"
            onClick={onClickCopyMd}
            loading={isCopyingMD}
          >
            Копировать
          </Button>
        </div>
      </div>
    </Modal>
  );
};
